import React from 'react';
import { Plus } from 'lucide-react';
import type { ServiceFeeRates } from '../../../types/selfService';
import { calculateImpressions, estimateServiceFee, estimateTotalWithServiceFee } from '../../../types/selfService';
import { formatMoney, formatMoneySmart } from './format';
import { tintFromAccent } from '../steps/_shared';

export interface PlaysBoost {
  label: string;
  pct: number; // 0–1
}

interface ImpressionsCalculatorProps {
  budget: number | null;
  cpm: number;
  serviceFeeRates: ServiceFeeRates;
  accentColor?: string;
  compact?: boolean;
  // Itemized list of multiplicative play-boost contributors (e.g. promo +
  // prepay). Compounded into the total. When empty / zero, the calculator
  // shows the plain baseline.
  boosts?: PlaysBoost[];
  // CTV-575: when the buyer picks the monthly plan, the review page passes the
  // sum of the per-installment charges here so "Total cost" agrees with the
  // "Total over N months" schedule below it (that sum legitimately exceeds the
  // single upfront gross-up — each installment carries its own fixed fee). When
  // null/undefined the calculator shows its own upfront gross-up. The Service
  // Fee row is back-derived so Budget + Fee still equals the shown Total.
  totalOverride?: number | null;
}

const ImpressionsCalculator: React.FC<ImpressionsCalculatorProps> = ({
  budget,
  cpm,
  serviceFeeRates,
  accentColor = '#3b82f6',
  compact = false,
  boosts = [],
  totalOverride = null,
}) => {
  if (!budget || budget <= 0) return null;

  const baseImpressions = calculateImpressions(budget, cpm);
  // Boosts stack ADDITIVELY on the base — each bonus is its own slice of
  // the original play count, not compounded onto the running total. So
  // 8% prepay + 10% promo = +18% off base, not +18.8%.
  const lineItems = boosts
    .filter((b) => b.pct > 0)
    .map((b) => ({
      label: b.label,
      pct: b.pct,
      delta: Math.floor(baseImpressions * b.pct),
    }));
  const totalBonus = lineItems.reduce((acc, l) => acc + l.delta, 0);
  const totalImpressions = baseImpressions + totalBonus;
  // CTV-518: tax is 0 everywhere today. The seam exists (estimate helpers
  // take taxCents and the Tax row below renders only when > 0) so enabling
  // sales tax later is a data change, not a UI build.
  const tax: number = 0;
  // CTV-575: monthly plan overrides the total with the installment-sum so the
  // card matches the schedule; the fee is then Total − Budget − Tax. Upfront
  // (override null) falls back to the single gross-up.
  const useOverride = totalOverride != null && totalOverride > 0;
  const total = useOverride
    ? totalOverride
    : estimateTotalWithServiceFee(budget, serviceFeeRates, tax);
  const fee = useOverride
    ? Math.round((total - budget - tax) * 100) / 100
    : estimateServiceFee(budget, serviceFeeRates, tax);
  // CTV-558: the per-charge percent + fixed cents now appears inline in the
  // Service Fee label itself, not as a separate hint string.
  const feeRateLabel = `Service Fee (${+(serviceFeeRates.percent * 100).toFixed(2)}% + $${formatMoney(serviceFeeRates.fixedCents / 100)})`;

  if (compact) {
    return (
      <div className="flex items-center gap-4 text-sm">
        <span className="text-slate-600">
          ~<span className="text-slate-900 font-medium">{totalImpressions.toLocaleString()}</span> ad plays
        </span>
        <span className="text-slate-400">|</span>
        <span className="text-slate-600">
          Total: <span className="text-slate-900 font-medium">${formatMoneySmart(total)}</span>
        </span>
      </div>
    );
  }

  const hasBonus = totalBonus > 0;

  return (
    <div
      className="rounded-2xl border border-slate-200/80 overflow-hidden bg-white"
      style={{ boxShadow: 'var(--card-shadow)' }}
    >
      {/* Hero */}
      <div className="px-5 pt-5 pb-4 flex items-baseline gap-3">
        <div className="flex-1">
          <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">
            {hasBonus ? 'Total ad plays' : 'Ad plays'}
          </div>
          <div
            className="mt-1 text-3xl font-semibold tracking-tight"
            style={{ color: accentColor }}
          >
            {totalImpressions.toLocaleString()}
          </div>
        </div>
        <div className="text-right">
          <div
            className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400"
            title="cost per 1,000 ad plays"
          >
            CPM
          </div>
          <div className="mt-1 text-[13px] font-medium text-slate-600 leading-tight">
            ${formatMoneySmart(hasBonus && totalImpressions > 0 ? (budget * 1000) / totalImpressions : cpm)}
            {hasBonus && (
              <div className="text-[11px] text-slate-400 line-through">
                ${formatMoneySmart(cpm)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stacked breakdown — base plays + each itemized bonus. Bonus rows
          get a tinted background and bigger emphasis so the buyer can
          immediately see what each code/plan is contributing. */}
      {hasBonus && (
        <div className="px-5 py-3 border-t border-slate-200/70 space-y-2 bg-slate-50/40">
          <div className="flex justify-between items-baseline text-[13px]">
            <span className="text-slate-500">Base plays</span>
            <span className="text-slate-700 font-medium tabular-nums">
              {baseImpressions.toLocaleString()}
            </span>
          </div>
          {lineItems.map((item, i) => (
            <div
              key={`${item.label}-${i}`}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border"
              style={{
                backgroundColor: tintFromAccent(accentColor, 0.08),
                borderColor: tintFromAccent(accentColor, 0.25),
              }}
            >
              <span className="inline-flex items-center gap-2 min-w-0">
                <span
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full shrink-0"
                  style={{ backgroundColor: accentColor }}
                >
                  <Plus className="w-3 h-3 text-white" strokeWidth={3} />
                </span>
                <span className="text-[13px] font-semibold text-slate-900 truncate">
                  {item.label}
                </span>
                <span
                  className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums tracking-tight"
                  style={{ backgroundColor: accentColor, color: 'white' }}
                >
                  +{Math.round(item.pct * 100)}%
                </span>
              </span>
              <span className="text-[15px] font-bold tabular-nums" style={{ color: accentColor }}>
                +{item.delta.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Cost breakdown. CTV-558: the percent + fixed-cents basis lives in
          the Service Fee label itself now, so no hint subtext below it. */}
      <div className="px-5 py-3 border-t border-slate-200/70 space-y-1.5">
        <Row label="Budget" value={`$${formatMoney(budget)}`} />
        <Row label={feeRateLabel} value={`$${formatMoney(fee)}`} />
        {tax > 0 && <Row label="Tax" value={`$${formatMoney(tax)}`} />}
      </div>

      {/* Total cost */}
      <div
        className="px-5 py-3 border-t border-slate-200/70 flex items-center justify-between"
        style={{ backgroundColor: tintFromAccent(accentColor, 0.04) }}
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600">
          Total cost
        </span>
        <span className="text-[15px] font-semibold tracking-tight text-slate-900">
          ${formatMoney(total)}
        </span>
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="flex justify-between items-baseline text-[13px]">
    <span className="text-slate-500">
      {label}
      {hint && <span className="ml-1.5 text-[10px] text-slate-400">{hint}</span>}
    </span>
    <span className="text-slate-700 font-medium tabular-nums">{value}</span>
  </div>
);

export default ImpressionsCalculator;
